import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/store';
import type { AppTab } from '@/types/navigation';

export const homeTab: AppTab = {
  key: '/home',
  label: '首页',
  closable: false,
};

interface NavigationState {
  activeKey: string;
  tabs: AppTab[];
}

const initialState: NavigationState = {
  activeKey: homeTab.key,
  tabs: [homeTab],
};

const ensureHomeTab = (tabs: AppTab[]) => {
  if (tabs.some((tab) => tab.key === homeTab.key)) {
    return tabs;
  }

  return [homeTab, ...tabs];
};

const navigationSlice = createSlice({
  name: 'navigation',
  initialState,
  reducers: {
    syncRoute(state, action: PayloadAction<AppTab>) {
      state.activeKey = action.payload.key;
      const existingTab = state.tabs.find((tab) => tab.key === action.payload.key);

      if (!existingTab) {
        state.tabs.push(action.payload);
        return;
      }

      existingTab.label = action.payload.label;
      existingTab.closable = action.payload.closable;
    },
    removeTab(state, action: PayloadAction<string>) {
      state.tabs = ensureHomeTab(
        state.tabs.filter((tab) => tab.key !== action.payload || !tab.closable),
      );
      if (!state.tabs.some((tab) => tab.key === state.activeKey)) {
        state.activeKey = homeTab.key;
      }
    },
    setActiveTab(state, action: PayloadAction<string>) {
      state.activeKey = action.payload;
    },
    resetTabs(state) {
      state.activeKey = homeTab.key;
      state.tabs = [homeTab];
    },
  },
});

export const getNextTabKeyAfterClose = (
  tabs: AppTab[],
  targetKey: string,
  activeKey: string,
) => {
  if (activeKey !== targetKey) {
    return activeKey;
  }

  const targetIndex = tabs.findIndex((tab) => tab.key === targetKey);
  const restTabs = tabs.filter((tab) => tab.key !== targetKey);
  return restTabs[targetIndex - 1]?.key ?? restTabs[targetIndex]?.key ?? homeTab.key;
};

export const navigationActions = navigationSlice.actions;
export const selectTabs = (state: RootState) => state.navigation.tabs;
export const selectActiveTab = (state: RootState) => state.navigation.activeKey;

export default navigationSlice.reducer;
